import { Button, Loading } from "@components/ui";
import { useLogout, useUser } from "@hooks/useAuth";
import { useDelayedFlag } from "@hooks/useDelayedFlag";
import { useAuthStore } from "@store/authStore";
import { ReactNode } from "react";

const WAKING_HINT_DELAY_MS = 8000;

export default function AuthBootstrapGate({
  children,
}: {
  children: ReactNode;
}) {
  // Gate on the STORE user (the source of truth the route guards read), not on
  // the query result. useUser() writes the store via an effect on success, so
  // gating on store.user guarantees guards see a populated user when children
  // mount — avoids a redirect race on the first post-success render.
  const user = useAuthStore((state) => state.user);
  const { isError, isFetching, refetch } = useUser();
  const logout = useLogout();

  const showWakingHint = useDelayedFlag(
    !user && !isError && isFetching,
    WAKING_HINT_DELAY_MS,
  );

  if (user) {
    return <>{children}</>;
  }

  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand-50 px-6">
        <div className="max-w-sm text-center">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">
            Não foi possível carregar seus dados
          </h1>
          <p className="mb-6 text-sm text-gray-500">
            Verifique sua conexão e tente novamente. Se o problema persistir,
            saia e entre novamente.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="primary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
            <Button variant="secondary" onClick={logout}>
              Sair
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand-50 px-6">
      <div className="max-w-sm text-center">
        <Loading message="Carregando..." />
        {showWakingHint && (
          <p className="mt-4 text-sm text-gray-500">
            O servidor pode estar reativando. Isso pode levar até ~1 minuto.
          </p>
        )}
      </div>
    </div>
  );
}
